import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../utils/imageUtils';
import {
  LayoutDashboard,

  BookOpen,
  FileText,
  User,
  LogOut,
  Menu,
  X,
  Bell,
  Settings,
  GraduationCap,
  ChevronRight,
  ChevronDown,
  Book,
  Upload,
  Users,
  MessageSquare,
  Video
} from 'lucide-react';
import AcademicResourcesFooter from './AcademicResourcesFooter';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [institutionName, setInstitutionName] = useState('');
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isLecturer = user?.role === 'lecturer';

  // Load institution data
  React.useEffect(() => {
    const fetchInstitutionName = async () => {
      if (user && user.campusId) {
        try {
          const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
          const response = await fetch(`${API_BASE_URL}/campuses/${user.campusId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          if (response.ok) {
            const campus = await response.json();
            // Prefer Institution name if available, otherwise fallback to Campus name
            const institution = campus.institution || campus.Institution;
            const displayName = institution?.name || campus.name;
            setInstitutionName(displayName);

            // Also store in localStorage for other pages to use (like Login/Signup before auth)
            localStorage.setItem('selectedInstitution', JSON.stringify({
              id: institution?.id || campus.institutionId,
              name: displayName
            }));
          }
        } catch (error) {
          console.error('Error fetching institution name:', error);
        }
      } else {
        // Fallback to localStorage if user is not available or has no campusId
        try {
          const selectedInstitutionString = localStorage.getItem('selectedInstitution');
          if (selectedInstitutionString) {
            const selectedInstitution = JSON.parse(selectedInstitutionString);
            setInstitutionName(selectedInstitution.name || '');
          }
        } catch (error) {
          console.error('Error loading institution data:', error);
        }
      }
    };

    fetchInstitutionName();
  }, [user]);

  const navigationItems = isLecturer ? [
    {
      to: '/lecturer-dashboard',
      icon: LayoutDashboard,
      title: 'Dashboard',
      isActive: location.pathname === '/lecturer-dashboard',
    },
    {
      to: '/lecturer-units',
      icon: BookOpen,
      title: 'Manage My Units',
      isActive: location.pathname === '/lecturer-units',
    },
    {
      to: '/materials',
      icon: Upload,
      title: 'Materials',
      isActive: location.pathname === '/materials',
    },
    {
      to: '/assignments',
      icon: FileText,
      title: 'Assignments',
      isActive: location.pathname === '/assignments',
    },
    {
      to: '/profile',
      icon: User,
      title: 'Profile',
      isActive: location.pathname === '/profile',
    },
  ] : [
    {
      to: '/dashboard',
      icon: LayoutDashboard,
      title: 'Dashboard',
      isActive: location.pathname === '/dashboard',
    },
    {
      to: '/units',
      icon: BookOpen,
      title: 'My Units',
      isActive: location.pathname === '/units',
    },
    {
      to: '/assignments',
      icon: FileText,
      title: 'Assignments',
      isActive: location.pathname === '/assignments',
    },
    {
      to: '/materials',
      icon: Book,
      title: 'Materials',
      isActive: location.pathname === '/materials',
    },
    {
      to: '/profile',
      icon: User,
      title: 'Profile',
      isActive: location.pathname === '/profile',
    },
  ];

  const NavigationItem = ({ to, icon: Icon, title, isActive }) => (
    <Link
      to={to}
      className={`
        flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 group
        ${isActive
          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg'
          : 'text-gray-700 hover:bg-gray-100 hover:text-primary-600'
        }
      `}
      onClick={() => setSidebarOpen(false)}
    >
      <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-gray-500 group-hover:text-primary-600'}`} />
      <span className="font-medium">{title}</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } md:translate-x-0`}
      >
        <div className="flex h-full w-64 md:w-56 lg:w-64 flex-col bg-white shadow-xl border-r border-gray-200">
          {/* Sidebar Close Button for Mobile */}
          <div className="flex justify-end p-4 md:hidden border-b border-gray-200">
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-md hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Logo Section */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg flex items-center justify-center">
                <GraduationCap className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {institutionName || 'Learning'} Portal
                </h2>
                <p className="text-sm text-gray-500">{isLecturer ? 'Lecturer' : 'Student'} Dashboard</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4">
              <div className="space-y-1">
                {navigationItems.map((item, index) => (
                  <NavigationItem key={index} {...item} />
                ))}
              </div>
            </div>
          </div>

          {/* User Profile Section */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center space-x-3 mb-4">
              {user?.profilePicture ? (
                <img
                  src={getImageUrl(user.profilePicture)}
                  alt="Profile"
                  className="w-10 h-10 rounded-full object-cover border-2 border-gray-100"
                />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </div>
              )}
              <div className="flex-1">
                <p className="font-semibold text-gray-900">{user?.name || (isLecturer ? 'Lecturer' : 'Student')}</p>
                <p className="text-sm text-gray-500">{user?.email || ''}</p>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 w-full px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 md:ml-56 lg:ml-64`}>
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100 transition-colors md:hidden"
              >
                <Menu className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
                  {institutionName || 'Learning'} Portal
                </h2>
                <p className="text-sm text-gray-500">{isLecturer ? 'Lecturer' : 'Student'} Dashboard</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <div className="relative">
                <button className="p-2 rounded-full hover:bg-gray-100 transition-colors relative">
                  <Bell className="w-5 h-5 text-gray-600" />
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    2
                  </span>
                </button>
              </div>

              {/* Profile Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {user?.profilePicture ? (
                    <img
                      src={getImageUrl(user.profilePicture)}
                      alt="Profile"
                      className="w-8 h-8 rounded-full object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-medium text-gray-900">{user?.name || 'Student'}</p>
                    <p className="text-xs text-gray-500">{user?.role || 'Student'}</p>
                  </div>
                </button>

                {/* Profile Dropdown Menu */}
                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <Link
                      to="/profile"
                      className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      onClick={() => setProfileDropdownOpen(false)}
                    >
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </Link>
                    <button className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                      <Settings className="w-4 h-4" />
                      <span>Settings</span>
                    </button>
                    <hr className="my-1" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className={`p-4 md:p-6 ${isLecturer ? 'pb-44 md:pb-36' : 'pb-20'}`}>
          <div className="max-w-6xl mx-auto">
            {children}
          </div>
        </main>

        {/* Persistent Footer for Lecturers */}
        {isLecturer && <AcademicResourcesFooter />}
      </div>
    </div>
  );
};

export default Layout;
